import "./App.css";
import { useEffect, useState } from "react";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";
import SalesOrder from "./Pages/SalesOrder/SalesOrder";
import Dashboard from "./Pages/Dashboard/Dashboard";
import Products from "./Pages/Products/Products";
import StockControl from "./Pages/StockControl/StockControl";
import MyApps from "./Pages/Apps/MyApps";
import User from "./Pages/User/UserProfile/User";
import Login from "./Pages/User/Login/Login";
import AddNewProducts from "./Pages/Products/AddNewProducts/AddNewProducts";
import CreateOrder from "./Pages/SalesOrder/CreateOrders/CreateOrder";
import Customers from "./Pages/Contacts/Customers";
import NewContacts from "./Pages/Contacts/NewContacts/NewContacts";
import ManageDeliveries from "./Pages/ManageDeliveries/ManageDeliveries";
import CreateDeliveries from "./Pages/ManageDeliveries/CreateDeliveries/CreateDeliveries";
import Warehouse from "./Pages/Warehouse/Warehouse";
import UpdateOrder from "./Pages/SalesOrder/UpdateOrder/UpdateOrder";
import UserContext from "./Context/UserContext";
import UpdateProduct from "./Pages/Products/UpdateProducts/UpdateProducts";
import UpdateShipment from "./Pages/Shipments/ShipmentUpdate/UpdateShipment";
import axios from "axios";
import { useContext } from "react";
import ForgotPassword from "./Pages/User/ForgotPassword/ForgotPassword";
import ResetPassword from "./Pages/User/ResetPassword/ResetPassword";
import UpdateContacts from "./Pages/Contacts/UpdateContacts/UpdateContacts";
import NewStock from "./Pages/StockControl/NewStock/NewStock";
import { BASE_URL } from "./Configuration/baseUrls";
import Register from "./Pages/User/Register/Register";
import UpdateDeliveries from "./Pages/ManageDeliveries/UpdateDeliveries/UpdateDelivers";
import ScheduledOrders from "./Pages/SalesOrder/RecurringOrders/ScheduledOrders";
import Runs from "./Pages/ManageDeliveries/Runs/Runs";
import CompletedOrders from "./Pages/SalesOrder/CompletedOrders/CompletedOrders";
import AllShipments from "./Pages/Shipments/AllShipments";
import CompletedShipments from "./Pages/Shipments/CompletedShipments";
import Navbar from "./Components/Shared/Navbar/Navbar";
import LoadingSpinner from "./Components/Shared/Loader/Loader";
import CompletedDeliveries from "./Pages/ManageDeliveries/CompletedDeliveries/CompletedDeliveries";
import CalculateStock from "./Pages/ManageDeliveries/CalculateStock/CalculateStock";
import StockHistory from "./Pages/SalesOrder/StockHistory/StockHistory";
import WarehousePrepare from "./Pages/ManageDeliveries/WarehousePrep/WarehousePrep";
// ✅ UPDATED: Replace InterState import with PickupOrders
import PickupOrders from "./Pages/SalesOrder/PickupOrders/PickupOrders";

function App() {
  const token = sessionStorage.getItem("token");
  const userRole = sessionStorage.getItem("userRole");
  let userEmail = sessionStorage.getItem("email"); // Try sessionStorage first
  
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { user, storeUser } = useContext(UserContext);
  
  // ✅ FALLBACK: If email not in sessionStorage, try user context
  if (!userEmail && user && user.email) {
    userEmail = user.email;
    console.log("📧 Using email from user context:", userEmail);
  }
  
  // ✅ DEBUG: Final email check
  console.log("🔍 Final userEmail value:", userEmail);

  const [sideToggle, setSideToggle] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initialCheckComplete, setInitialCheckComplete] = useState(false);

  // ✅ NEW: Special access users
  const specialAccessUsers = {
    "jason@firebrandbbq.com.au": {
      additionalRoutes: ["/pickupOrders"],
      displayName: "Jason (Special Access)",
    },
    // You can add more special users here:
    // "another@email.com": { additionalRoutes: ["/someOtherPage"] }
  };

  useEffect(() => {
    // Set loading state to true initially
    setLoading(true);

    if (!token) {
      setIsLoggedIn(false);
      // Set loading state and initial check state to false after authentication check
      setLoading(false);
      setInitialCheckComplete(true);
    } else {
      axios
        .post(`${BASE_URL}user/tokenVerifier`, { token })
        .then((res) => {
          if (res.data.active) {
            setIsLoggedIn(true);
            storeUser(res.data);
          } else {
            setIsLoggedIn(false);
          }

          // Set loading state and initial check state to false after authentication check
          setLoading(false);
          setInitialCheckComplete(true);
        })
        .catch((error) => {
          console.error("Error during authentication check:", error);
          // Set loading state and initial check state to false even in case of an error
          setLoading(false);
          setInitialCheckComplete(true);
        });
    }
  }, []);

  // Define roles and corresponding allowed routes
  const roles = {
    admin: [
      "/",
      "/salesOrders",
      "/salesOrders/newOrder",
      "/salesOrders/updateOrder",
      "/products",
      "/products/UpdateProduct",
      "/products/Addnewproducts",
      "/stockControl",
      "/stockControl/newStock",
      "/shipments",
      "/allShipments",
      "/completedShipments",
      "/shipments/updateshipment",
      "/deliveries",
      "/deliveries/CompletedDeliveries",
      "/deliveries/createDeliveries",
      "/deliveries/calculateStock",
      "/apps",
      "/user",
      "/customers",
      "/customers/newCustomer",
      "/customers/updateCustomer",
      "/Warehouse",
      "/register",
      "/updateDeliveries",
      "/scheduledOrders",
      "/completedOrders",
      "/stockHistory",
      "/warehousePrepare",
      "/pickupOrders",
    ],
    user: [
    "/", // Dashboard
    "/salesOrders", // See all orders
    "/salesOrders/newOrder", // Create order
    "/salesOrders/updateOrder", // Update order
    "/customers", // See all customers
    "/customers/newCustomer", // Create customer
    "/customers/updateCustomer", // Update customer
    "/user", // User profile
  ],
    delivery: ["/runs", "/warehousePrepare"],
  };

  // ✅ NEW: Get base allowed routes and add special access
  let allowedRoutes = roles[userRole] || [];

  // ✅ NEW: Add special access routes for specific users
  if (userEmail && specialAccessUsers[userEmail]) {
    const specialAccess = specialAccessUsers[userEmail];
    allowedRoutes = [...allowedRoutes, ...specialAccess.additionalRoutes];

    // Remove duplicates
    allowedRoutes = [...new Set(allowedRoutes)];

    console.log(
      `🔑 Special access granted to ${userEmail}: ${specialAccess.additionalRoutes.join(
        ", "
      )}`
    );
  }

  return (
    <>
      {initialCheckComplete ? (
        <>
          {isLoggedIn ? (
            <Navbar
              data={{
                sideToggle,
                roles,
                userRole,
                setIsLoggedIn,
                // ✅ NEW: Pass special access info to Navbar
                specialAccess:
                  userEmail && specialAccessUsers[userEmail]
                    ? specialAccessUsers[userEmail]
                    : null,
                userEmail,
              }}
              setSideToggle={setSideToggle}
            />
          ) : null}
          <div
            className={
              isLoggedIn
                ? sideToggle
                  ? "content_toggle"
                  : "content"
                : "loggedIn"
            }
          >
            <Routes>
              {isLoggedIn ? (
                <Route>
                  {allowedRoutes.map((route) => (
                    <Route
                      key={route}
                      path={route}
                      element={getRouteComponent(route)}
                    />
                  ))}
                </Route>
              ) : (
                <Route>
                  <Route
                    path="/login"
                    element={<Login setIsLoggedIn={setIsLoggedIn} />}
                  />
                  <Route
                    path="/forgotPassword"
                    element={<ForgotPassword setIsLoggedIn={setIsLoggedIn} />}
                  />
                  <Route
                    path="/user/:id/password-reset/:token"
                    element={<ResetPassword setIsLoggedIn={setIsLoggedIn} />}
                  />
                  <Route
                    path="/*"
                    element={
                      isLoggedIn ? (
                        <Dashboard />
                      ) : (
                        <Login setIsLoggedIn={setIsLoggedIn} />
                      )
                    }
                  />
                </Route>
              )}
              <Route
                path="/"
                element={
                  isLoggedIn ? (
                    <Dashboard />
                  ) : (
                    <Login setIsLoggedIn={setIsLoggedIn} />
                  )
                }
              />
            </Routes>
          </div>
        </>
      ) : (
        <LoadingSpinner />
      )}
    </>
  );
}

// Helper function to get the component for a given route
function getRouteComponent(route) {
  switch (route) {
    case "/salesOrders":
      return <SalesOrder />;
    case "/salesOrders/newOrder":
      return <CreateOrder />;
    case "/salesOrders/updateOrder":
      return <UpdateOrder />;
    case "/salesOrders/manageDeliveries":
      return <ManageDeliveries />;
    case "/products":
      return <Products />;
    case "/products/UpdateProduct":
      return <UpdateProduct />;
    case "/products/Addnewproducts":
      return <AddNewProducts />;
    case "/stockControl":
      return <StockControl />;
    case "/stockControl/newStock":
      return <NewStock />;
    case "/completedShipments":
      return <CompletedShipments />;
    case "/allShipments":
      return <AllShipments />;
    case "/shipments/updateshipment":
      return <UpdateShipment />;
    case "/deliveries":
      return <ManageDeliveries />;
    case "/deliveries/createDeliveries":
      return <CreateDeliveries />;
    case "/deliveries/CompletedDeliveries":
      return <CompletedDeliveries />;
    case "/deliveries/calculateStock":
      return <CalculateStock />;
    case "/apps":
      return <MyApps />;
    case "/user":
      return <User />;
    case "/customers":
      return <Customers />;
    case "/customers/newCustomer":
      return <NewContacts />;
    case "/customers/updateCustomer":
      return <UpdateContacts />;
    case "/Warehouse":
      return <Warehouse />;
    case "/register":
      return <Register />;
    case "/":
      return <Dashboard />;
    case "/updateDeliveries":
      return <UpdateDeliveries />;
    case "/scheduledOrders":
      return <ScheduledOrders />;
    case "/runs":
      return <Runs />;
    case "/warehousePrepare":
      return <WarehousePrepare />;
    case "/completedOrders":
      return <CompletedOrders />;
    // ✅ UPDATED: Replace /interState case with /pickupOrders
    case "/pickupOrders":
      return <PickupOrders />;
    default:
      return null;
  }
}

export default App;
